import React, { useEffect, useState, useRef, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Thumbs } from "swiper/modules";
import { showError } from "toast/toast";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Product } from "types";
import { CartItem, Dimensions } from "types";
import { Variant } from "types";
import { toast, ToastContainer } from "react-toastify";
import { useCart } from "components/CartContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ReviewModal } from "components/ReviewModal";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import "../../assets/css/SignageOrders.css";
// add this next to your other imports
import { getCloudFileById, uploadFilesAndGetIds } from "components/Imageshelper";
import { getDiscountsByVariant, getStartingAt } from "components/Volumediscountcalls";
import type { VolumeDiscount as VD } from "components/Volumediscountcalls";
import TemplateDesignsSection from "components/TemplateDesignsSection";
import { getAuthCookie } from "utils/authCookies";
import { buildCartItemTaxInfo } from "utils/buildCartItemTaxInfo";
type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};
type Review = {
  id: string;
  user_name: string;
  rating: number;         // 1..5
  date: string;           // ISO
  title?: string;
  body: string;
  verified?: boolean;
  recommends?: boolean;
  helpful_yes?: number;
  helpful_no?: number;
};
type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type TaxRule = {
  id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

type StorageLocation = {
  bin_id: string;
  bin_name: string;
  rack_id: string;
  rack_name: string;
  warehouse_id: string;
  warehouse_name: string;
  zone_id: string;
  zone_name: string;
  on_hand: number;
  available: number;
  reserved: number;
};

type VolumeDiscountTier = {
  id: string;
  starting_at: number;
  discount_in?: "%" | "$";
  discount?: number;
  fixed_price?: number;
};

function normalizeDiscountAssignments(list: any[]): VolumeDiscountTier[] {
  return (Array.isArray(list) ? list : [])
    .flatMap((r: any) => {
      // rows shaped like: { ..., starting_at, status, discount: { discount_in, discount, fixed_price, status, ... } }
      const d = r?.discount ?? r; // fallback: support old flat rows too

      // both the assignment row and the discount must be active (default = active)
      const assignmentActive = (r?.status ?? "active") === "active";
      const discountActive = (d?.status ?? "active") === "active";
      if (!assignmentActive || !discountActive) return [];

      // Prefer starting_at from inside discount object, fallback to assignment level
      const starting_at = Number(d?.starting_at ?? r?.starting_at ?? 1);

      // ensure there is some price-affecting data
      const hasPctOrAmt = typeof d?.discount === "number" && typeof d?.discount_in === "string";
      const hasFixed = typeof d?.fixed_price === "number";
      if (!hasPctOrAmt && !hasFixed) return [];

      return [{
        id: String(r?.id ?? r?.discount_id ?? d?.id ?? starting_at),
        starting_at,
        discount_in: hasPctOrAmt ? (d.discount_in as "%" | "$") : undefined,
        discount: hasPctOrAmt ? d.discount : undefined,
        fixed_price: hasFixed ? d.fixed_price : undefined,
      }];
    })
    .sort((a, b) => a.starting_at - b.starting_at);
}

const AgentViewSignageVariableProduct = () => {
  const { productId } = useParams<{ productId: string }>();
  const { signageId } = useParams<{ signageId: string }>();
  const [searchParams] = useSearchParams();
  /** Return from Studio: all three values mean the variant already has a customized design payload. */
  const studioCustomizeDesignId = searchParams.get("customizeDesignId")?.trim() ?? "";
  const studioPreviewId = searchParams.get("previewId")?.trim() ?? "";
  const studioDesignName = searchParams.get("designName")?.trim() ?? "";
  const isStudioCustomizedProduct = Boolean(
    studioCustomizeDesignId && studioDesignName && studioPreviewId
  );
  /** Work-drive GET /work-drive/files/:id → presigned_url (customized design preview only). */
  const [studioPreviewPresignedUrl, setStudioPreviewPresignedUrl] = useState<string | null>(null);
  const [studioPreviewLoadFailed, setStudioPreviewLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [productImages, setProductImages] = useState<any[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false); // for upcoming popup
  // Modal for add-to-cart confirmation
  const [showModal, setShowModal] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const navigate = useNavigate();
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [availableAttributes, setAvailableAttributes] = useState<
    Record<string, string[]>
  >({});
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const { fetchCart } = useCart(); // Fetch cart items from context
  const [customizationFields, setCustomizationFields] = useState<any[]>([]);
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const [volumeDiscounts, setVolumeDiscounts] = useState<VD[]>([]);

  // Review summary state
  type ReviewSummary = {
    product_id: string;
    product_variant_id: string;
    average_rating: number;
    rating_count: number;
  };
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [loadingReviewSummary, setLoadingReviewSummary] = useState(false);

  const handleIncrease = () => setQuantity((prev) => prev + 1); // Increase quantity
  const handleDecrease = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1)); // Decrease quantity

  const handleAddToCart = async (product: CartItem) => {
    // console.log("Add to cart product:", product);

    let currentCartId = localStorage.getItem("cart_id") || "";

    try {
      let response;
      if (!currentCartId) {
        // 🔹 Create new cart
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/carts`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              cart_items: [product],
              is_active: true,
              supplier_id: signageId || "",
            }),
          });

        if (!response.ok) throw new Error("Failed to add data");
        const result = await response.json();
        if (result.cart_id) {
          localStorage.setItem("cart_id", result.cart_id);
        }
      } else {
        // 🔹 Add to existing cart
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/carts/${currentCartId}/add-product`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(product),
          }
        );

        if (!response.ok) throw new Error("Failed to add data");
        const result = await response.json();
      }
      fetchCart();
      toast.success("Product added to cart successfully!");
      setShowModal(true);
    } catch (err) {
      console.error(err instanceof Error ? err.message : "Unknown error");
    }
  };

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch product data");
        }

        const data: ProductObj = await response.json();
        // setProduct(data);
        setProductObj(data);
        setProductImages(data.product.images ? data.product.images : []);
        setDetailsHtml(data.product.description ? data.product.description : "");
        // console.log("Product Images:", data.product.images || []);

        const variantCustomizationFields =
          data?.product?.variants?.[0]?.customizations?.fields || [];
        setCustomizationFields(variantCustomizationFields);

      } catch (err) {
        console.error("Error fetching product data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (productId) fetchProduct();
  }, [productId, token]);

  // Fetch review summary
  const fetchReviewSummary = async (prodId: string, variantId: string) => {
    if (!process.env.REACT_APP_API_URL || !token || !prodId || !variantId) return;

    try {
      setLoadingReviewSummary(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/product-reviews/summary`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                product_id: prodId,
                product_variant_id: variantId,
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch review summary");
      }

      const data = await response.json();
      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        setReviewSummary(data.data[0]);
      } else {
        setReviewSummary(null);
      }
    } catch (err) {
      console.error("Error fetching review summary:", err);
      setReviewSummary(null);
    } finally {
      setLoadingReviewSummary(false);
    }
  };

  // Fetch review summary when product/variant data is available
  useEffect(() => {
    const prodId = productobj?.product_id;
    const variantId = selectedVariant?.variant_id || productobj?.product?.variants?.[0]?.variant_id;
    if (prodId && variantId) {
      fetchReviewSummary(prodId, variantId);
    }
  }, [productobj?.product_id, selectedVariant?.variant_id, productobj?.product?.variants?.[0]?.variant_id, token]);


  const [imageCache, setImageCache] = useState<{ product: any[]; variants: Record<string, any[]> }>({
    product: [],
    variants: {}
  });

  // Default: no variant selected, every attribute shows "All"
  useEffect(() => {
    if (productobj?.product?.variants?.length) {
      const attributesMap: Record<string, string> = {};
      Object.keys(productobj.product.variants[0].attributes || {}).forEach(k => {
        attributesMap[k] = "All";
      });
      setSelectedVariant(null);
      setSelectedAttributes(attributesMap);
      setAttrPicked(false);
    }
  }, [productobj]);



  // Track if the user actually changed an attribute (so we keep product images first)
  const [attrPicked, setAttrPicked] = useState(false);

  // Presign IDs for product / variant gallery images (not used for studio preview — see work-drive fetch below)
  const dataForMap = useMemo(() => {
    const prodImgs = (productobj?.product?.images || []).map((m: any) => ({ url: m?.url }));
    const varImgs = (productobj?.product?.variants || [])
      .flatMap((v: any) => (Array.isArray(v.images) ? v.images : []))
      .map((m: any) => ({ url: m?.url }));

    const map = new Map<string, { url: string }>();
    [...prodImgs, ...varImgs].forEach(m => { if (m?.url) map.set(m.url, { url: m.url }); });
    const all = Array.from(map.values());

    return all.length
      ? [{ product: { variants: [{ images: all }] } }]
      : [];
  }, [productobj]);

  // Presigned URL map { [imageId]: "https://...signed" }
  const imageUrlMap = useVariantImageUrlMap(dataForMap, getAuthCookie("sessionToken") || "");

  useEffect(() => {
    if (!isStudioCustomizedProduct || !studioPreviewId || !token) {
      setStudioPreviewPresignedUrl(null);
      setStudioPreviewLoadFailed(false);
      return;
    }
    let cancelled = false;
    setStudioPreviewPresignedUrl(null);
    setStudioPreviewLoadFailed(false);
    (async () => {
      try {
        const file = await getCloudFileById(studioPreviewId, token);
        if (cancelled) return;
        if (file.url) setStudioPreviewPresignedUrl(file.url);
        else setStudioPreviewLoadFailed(true);
      } catch {
        if (!cancelled) setStudioPreviewLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStudioCustomizedProduct, studioPreviewId, token]);

  const resolveGallery = React.useCallback(() => {
    // Before user interaction OR when any "All" present → outside images
    if (!attrPicked) return imageCache.product;

    // After pick: show matched variant images (even if empty)
    if (selectedVariant?.variant_id) {
      return imageCache.variants[selectedVariant.variant_id] || [];
    }

    // No matched variant → empty
    return [];
  }, [attrPicked, selectedVariant?.variant_id, imageCache]);

  useEffect(() => {
    const imgs = resolveGallery();
    setProductImages(imgs);
    setActiveIndex(0);
    swiperRef.current?.slideTo?.(0);
  }, [resolveGallery]);

  useEffect(() => {
    if (productobj?.product?.variants?.length) {
      // Collect all unique attribute keys and their possible values
      const attributesMap: Record<string, Set<string>> = {};

      productobj.product.variants.forEach((variant) => {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributesMap[key]) {
            attributesMap[key] = new Set();
          }
          attributesMap[key].add(value);
        });
      });
      // Convert sets to arrays
      const attributesWithOptions: Record<string, string[]> = {};
      Object.keys(attributesMap).forEach((key) => {
        attributesWithOptions[key] = Array.from(attributesMap[key]);
      });

      setAvailableAttributes(attributesWithOptions);
    }
  }, [productobj]);

  const trimAttrObj = (obj: Record<string, any> = {}) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? "").trim()]));

  // strict match on all keys after trimming
  const findVariantByAttributes = (variants: any[], selected: Record<string, string>) => {
    const want = trimAttrObj(selected);
    return (variants || []).find(v => {
      const va = trimAttrObj(v.attributes || {});
      return Object.keys(want).every(k => want[k] === (va[k] ?? ""));
    }) || null;
  };

  const handleAttributeChange = (key: string, value: string) => {
    const updated = { ...selectedAttributes, [key]: value };
    setSelectedAttributes(updated);

    const anyAll = Object.values(updated).some(v => v === "All");
    if (anyAll) {
      setAttrPicked(false);
      setSelectedVariant(null);
      return;
    }
    setAttrPicked(true);
    const matched = findVariantByAttributes(productobj?.product?.variants || [], updated);
    setSelectedVariant(matched);
  };



  const formik = useFormik({
    enableReinitialize: true,
    initialValues: customizationFields.reduce((acc, field) => {
      acc[field.field_name] = field.field_type === "file" ? null : "";
      return acc;
    }, {} as Record<string, string | File | null>),

    validationSchema: Yup.object(
      customizationFields.reduce((acc, field) => {
        if (field.required) {
          acc[field.field_name] =
            field.field_type === "file"
              ? Yup.mixed().required("Required")
              : Yup.string().required("Required");
        }
        return acc;
      }, {} as Record<string, any>)
    ),

    onSubmit: () => { }, // no-op, you control submission manually
  });

  useEffect(() => {
    if (productobj?.product?.variants?.length) {
      // Collect all unique attribute keys and their possible values
      const attributesMap: Record<string, Set<string>> = {};

      productobj.product.variants.forEach((variant) => {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributesMap[key]) {
            attributesMap[key] = new Set();
          }
          attributesMap[key].add(value);
        });
      });
      // Convert sets to arrays
      const attributesWithOptions: Record<string, string[]> = {};
      Object.keys(attributesMap).forEach((key) => {
        attributesWithOptions[key] = Array.from(attributesMap[key]);
      });

      setAvailableAttributes(attributesWithOptions);
    }
  }, [productobj]);

  // Set the first variant as default
  useEffect(() => {
    if (productobj?.product?.variants?.length) {
      const defaultVariant = productobj.product.variants[0];
      setSelectedVariant(defaultVariant);

      // Set dropdowns to the default variant’s attributes
      const defaultAttributes: { [key: string]: string } = {};
      Object.entries(defaultVariant.attributes).forEach(([key, value]) => {
        defaultAttributes[key] = value;
      });

      setSelectedAttributes(defaultAttributes);
    }
  }, [productobj]);



  const findVariantDetails = (
    variants: Variant[],
    selectedAttributes: Record<string, string>
  ): Variant | undefined => {
    return variants?.find((variant) =>
      Object.entries(selectedAttributes).every(
        ([attrKey, attrValue]) => variant.attributes[attrKey] === attrValue
      )
    );
  };
  const hasRequiredFields = customizationFields.some(f => f.required === true);

  const [descTab, setDescTab] = useState<'specs' | 'details' | 'reviews'>('specs');

  const [detailsHtml, setDetailsHtml] = useState<string>('');     // HTML or text
  const [reviews, setReviews] = useState<Review[]>([]);
  const [descLoading, setDescLoading] = useState(false);
  const [descError, setDescError] = useState<string>('');

  // optional: build spec rows from product data
  const specLeft = useMemo(() => ([
    { k: 'Category', v: productobj?.product?.category || '-' },
    { k: 'Brand', v: productobj?.product?.brand || '-' },
    { k: 'Item Condition', v: productobj?.product?.item_condition || '-' },
    // { k: 'Material', v: productobj?.product?.material || '4mm Corrugated Plastic' },
  ]), [productobj]);

  const specRight = useMemo(() => ([
    { k: 'Printing', v: productobj?.product || 'UV Resistant Digital Print' },
    { k: 'Finish', v: productobj?.product || 'Weather Resistant Coating' },
    { k: 'Mounting', v: productobj?.product || 'H-Stakes Available' },
    { k: 'Turnaround', v: productobj?.product || '1-3 Business Days' },
  ]), [productobj]);

  // lazy-load on tab change
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setDescError('');
      // if (descTab === 'details') {
      //   if (detailsHtml) return;
      //   setDescLoading(true);
      //   try {
      //     // TODO: adjust endpoint if different in your API
      //     const r = await fetch(
      //       `${process.env.REACT_APP_API_URL}/signage/${signageId}/products/${productId}/details`,
      //       { headers: { Authorization: `Bearer ${token}` } }
      //     );
      //     if (!r.ok) throw new Error('Failed to load details');
      //     const data = await r.json();
      //     if (!cancelled) setDetailsHtml(data?.html || data?.text || '');
      //   } catch (e: any) {
      //     if (!cancelled) setDescError(e?.message || 'Failed to load details');
      //   } finally {
      //     if (!cancelled) setDescLoading(false);
      //   }
      // }

      if (descTab === 'reviews') {
        if (reviews.length) return;
        setDescLoading(true);
        try {
          // TODO: adjust endpoint if different in your API
          const r = await fetch(
            `${process.env.REACT_APP_API_URL}/signage/${signageId}/products/${productId}/reviews`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!r.ok) throw new Error('No Reviews Available');
          const data = await r.json();
          const list: Review[] = Array.isArray(data?.reviews) ? data.reviews : (data || []);
          if (!cancelled) setReviews(list);
        } catch (e: any) {
          if (!cancelled) setDescError(e?.message || 'No Reviews Available');
        } finally {
          if (!cancelled) setDescLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [descTab, reviews.length, token, productId, signageId]);

  // lazy-load on tab change
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setDescError('');
      // if (descTab === 'details') {
      //   if (detailsHtml) return;
      //   setDescLoading(true);
      //   try {
      //     // TODO: adjust endpoint if different in your API
      //     const r = await fetch(
      //       `${process.env.REACT_APP_API_URL}/signage/${signageId}/products/${productId}/details`,
      //       { headers: { Authorization: `Bearer ${token}` } }
      //     );
      //     if (!r.ok) throw new Error('Failed to load details');
      //     const data = await r.json();
      //     if (!cancelled) setDetailsHtml(data?.html || data?.text || '');
      //   } catch (e: any) {
      //     if (!cancelled) setDescError(e?.message || 'Failed to load details');
      //   } finally {
      //     if (!cancelled) setDescLoading(false);
      //   }
      // }

      if (descTab === 'reviews') {
        if (reviews.length) return;
        setDescLoading(true);
        try {
          // TODO: adjust endpoint if different in your API
          const r = await fetch(
            `${process.env.REACT_APP_API_URL}/signage/${signageId}/products/${productId}/reviews`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!r.ok) throw new Error('No Reviews Available');
          const data = await r.json();
          const list: Review[] = Array.isArray(data?.reviews) ? data.reviews : (data || []);
          if (!cancelled) setReviews(list);
        } catch (e: any) {
          if (!cancelled) setDescError(e?.message || 'No Reviews Available');
        } finally {
          if (!cancelled) setDescLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [descTab, reviews.length, token, productId, signageId]);


  const mapProductReview = (r: any): Review => ({
    id: r.id,
    user_name: r.user_name || r.created_by?.name || "User",
    rating: Number(r.rating) || 0,
    date: r.created_at ? new Date(r.created_at).toISOString() : new Date().toISOString(), // ← show created_at
    title: r.review_title || "",
    body: r.review_comment || "",
    // API doesn’t provide these; omit chips by leaving undefined
    verified: undefined,
    recommends: undefined,
    helpful_yes: 0,
    helpful_no: 0,
  });


  const loadAllProductReviews = async () => {
    if (!process.env.REACT_APP_API_URL || !token) return;
    setDescLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/product-reviews`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("No Reviews Available");
      const data = await res.json();

      const arr: any[] = Array.isArray(data) ? data : Array.isArray((data as any)?.data) ? (data as any).data : [];

      // keep only reviews for this product
      const filtered = productId ? arr.filter((r) => r.product_id === productId) : arr;

      const list: Review[] = filtered.map(mapProductReview);
      setReviews(list);
      setDescError("");
    } catch (e: any) {
      setDescError(e?.message || "No Reviews Available");
    } finally {
      setDescLoading(false);
    }
  };

  useEffect(() => {
    if (descTab === "reviews") {
      loadAllProductReviews();
    }
    // eslint-disable-next-line
  }, [descTab, productId]);

  const money = (n: number) => `$${(Math.max(0, n) || 0).toFixed(2)}`;

  /** Compute a unit price given base price + one discount row */
  function unitPriceFromDiscount(basePrice: number, d: Partial<VD> & { fixed_price?: number }): number {
    const fixed = (d as any)?.fixed_price;
    if (typeof fixed === "number" && !isNaN(fixed)) return Math.max(0, fixed);

    const disc = Number(d?.discount ?? 0);
    const inType = (d?.discount_in || "").toString();
    if (inType === "%") return Math.max(0, basePrice * (1 - disc / 100));
    // "amount" | "flat" (or anything else) => subtract
    return Math.max(0, basePrice - disc);
  }

  /** Pick the best discount (largest starting_at <= qty) */
  function bestForQty(discounts: VD[], qty: number): VD | null {
    const q = Number(qty) || 1;
    const c = discounts.filter((d) => getStartingAt(d) <= q);
    if (!c.length) return null;
    return c.reduce((a, b) => (getStartingAt(a) >= getStartingAt(b) ? a : b));
  }



  const idOf = (x?: any): string => (typeof x === "string" ? x : (x?.url ?? "")) || "";
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };
  const getProductImageSrc = (img?: any) => {
    const raw = idOf(img);
    return getImageSrc(raw, imageUrlMap) || buildImageUrl(raw) || imageCustomSquare;
  };

  const productImageIds = React.useMemo(
    () => (productImages || []).map((img: any) => idOf(img)).filter(Boolean),
    [productImages]
  );

  const dataForMap1 = React.useMemo(() => {
    if (!productImageIds.length) return [];
    return [{ product: { variants: [{ images: productImageIds.map((url) => ({ url })) }] } }];
  }, [productImageIds]);

  const imageUrlMap1 = useVariantImageUrlMap(dataForMap1, token);

  // 🔧 bump when map content changes (even if the object ref is stable)
  const mapVersion = React.useMemo(
    () => (imageUrlMap1 ? Object.keys(imageUrlMap1).length : 0),
    [imageUrlMap]
  );

  // ✅ will re-run when productImages change OR when map gets populated
  const resolvedImages = React.useMemo(
    () =>
      (productImages || []).map((img: any) => {
        const id = idOf(img);
        return {
          src: getImageSrc(id, imageUrlMap) || buildImageUrl(id),
          alt: img?.alt_text || "",
        };
      }),
    [productImages, mapVersion]   // <-- changed here
  );


  const [customPreviews, setCustomPreviews] = React.useState<Record<string, string>>({});

  // cleanup blob URLs on unmount
  React.useEffect(() => {
    return () => {
      Object.values(customPreviews).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { }
      });
    };
  }, [customPreviews]);

  function normalizeVariantAttributes(variant: any): Array<{ name: string; value: string }> {
    let raw: any =
      variant?.attributes ??
      variant?.variant_attributes ??
      variant?.variantAttributes ??
      variant?.attribute_values ??
      [];

    // string? try to parse JSON
    if (typeof raw === "string") {
      try { raw = JSON.parse(raw); } catch { raw = []; }
    }

    // object map -> turn into [{name,value}]
    if (raw && !Array.isArray(raw) && typeof raw === "object") {
      return Object.entries(raw).map(([name, value]) => ({ name, value: String(value) }));
    }

    // array -> normalize possible shapes
    if (Array.isArray(raw)) {
      return raw
        .map((a: any) => ({
          name: a?.name ?? a?.attribute_name ?? a?.key ?? "",
          value: a?.value ?? a?.attribute_value ?? a?.val ?? "",
        }))
        .filter((x) => x.name && x.value);
    }

    return [];
  }

  const variants = productobj?.product?.variants ?? [];
  useEffect(() => {
    if (!variants.length || selectedVariant) return;

    const v0 = variants[0];
    const init: Record<string, string> = {};
    normalizeVariantAttributes(v0).forEach(({ name, value }) => { init[name] = value; });

    // ensure every attribute has a default from availableAttributes
    Object.keys(availableAttributes).forEach((k) => {
      if (!init[k]) init[k] = availableAttributes[k]?.[0] ?? "";
    });

    setSelectedAttributes(init);
    setSelectedVariant(v0);
  }, [variants]); // minimal deps
  // minimal deps

  useEffect(() => {
    const pid = productId || productobj?.product_id || "";
    const vid = selectedVariant?.variant_id || "";
    if (!pid || !vid || !token) { setVolumeDiscounts([]); return; }

    let alive = true;
    (async () => {
      try {
        const raw = await getDiscountsByVariant(pid, vid, token);

        // Keep rows that apply to this variant or to all variants
        const scoped = (raw || []).filter((r: any) =>
          r?.apply_to_all_variants === true || r?.variant_id === vid || r?.variant_id == null
        );

        const normalized = normalizeDiscountAssignments(scoped);
        if (alive) setVolumeDiscounts(normalized as any); // state is VD[], normalized matches fields used by pricing logic
      } catch {
        if (alive) setVolumeDiscounts([]);
      }
    })();

    return () => { alive = false; };
  }, [productId, productobj?.product_id, selectedVariant?.variant_id, token]);


  const basePrice = Number(selectedVariant?.retail_price || productobj?.product?.retail_price || 0);
  const chosen = bestForQty(volumeDiscounts, quantity);
  const discountedUnitPrice = chosen ? unitPriceFromDiscount(basePrice, chosen) : basePrice;

  // Use the same money() helper you already have
  const modalQty = Number(quantity || 1);
  const modalUnitPrice = Number(discountedUnitPrice || 0);
  const modalSubtotal = modalUnitPrice * modalQty;
  const img0 = productobj?.product?.images?.[0];
  const firstId = idOf(img0);
  const fallbackId = idOf(productobj?.product?.images?.[0]);
  const src =
    getImageSrc(firstId, imageUrlMap) ||
    getImageSrc(fallbackId, imageUrlMap) ||
    buildImageUrl(firstId || fallbackId);
  const modalTitle = selectedVariant?.variant_name || productobj?.product?.name || "Product";
  const modalSku = selectedVariant?.sku || productobj?.product?.sku || "";

  const studioBaseUrl = (process.env.REACT_APP_STUDIO_BASE_URL || "").trim().replace(/\/+$/, "");
  const openStudioEditDesign = () => {
    if (!studioBaseUrl || !studioCustomizeDesignId) return;
    const returnPath = `${window.location.pathname}${window.location.search || ""}`;
    const studioUrl = new URL(
      `${studioBaseUrl}/open-design/${encodeURIComponent(studioCustomizeDesignId)}`
    );
    studioUrl.searchParams.set("return_path", returnPath);
    window.location.assign(studioUrl.toString());
  };

  return (
    <div className="pd-page container-xl">
      <div className="mb-4">
        <button type="button" className="pd-back" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line me-2" />
          Back to Products
        </button>
      </div>
      <h2 className="pd-title">Product Details</h2>

      <div className="row g-4 align-items-start">
        <div className="col-lg-6">
          {isStudioCustomizedProduct && studioPreviewId ? (
            <>
              <div className="gall-card shadow-lg">
                <div className="gall-main">
                  {studioPreviewPresignedUrl ? (
                    <img
                      className="gall-img"
                      src={studioPreviewPresignedUrl}
                      alt="Customized design preview"
                    />
                  ) : studioPreviewLoadFailed ? (
                    <div className="text-muted small px-2 py-5 text-center">
                      Unable to load customized preview.
                    </div>
                  ) : (
                    <div className="text-muted small px-2 py-5 text-center">Loading preview…</div>
                  )}
                </div>
              </div>
              <div className="col-lg-12 text-center">
              <button
                type="button"
                className="btn btn-primary table-addbutton fw-bold fs-16 mt-3"
                disabled={!studioBaseUrl || !studioCustomizeDesignId}
                onClick={openStudioEditDesign}
              >
                Edit the Design
              </button>
              </div>
            </>
          ) : !Array.isArray(productImages) || productImages.length === 0 ? (
            <div className="gall-card shadow-lg">
              <div className="gall-main">
                <img className="gall-img" src={imageCustomSquare} alt="No image" />
              </div>
            </div>
          ) : (
            Array.isArray(productImages) && productImages.length > 0 && (
              <div className="gall-card shadow-lg">
                <div className="gall-main">
                  <Swiper
                    key={productImages.map((img: any) => img?.url || "").join("|")}
                    onSwiper={(s: any) => (swiperRef.current = s)}
                    onSlideChange={(s: any) => setActiveIndex(s.activeIndex)}
                    slidesPerView={1}
                    spaceBetween={8}
                    className="gall-swiper"
                  >
                    {productImages.map((img: any, i: number) => (
                      <SwiperSlide key={i}>
                        <img
                          className="gall-img"
                          src={getProductImageSrc(img)}
                          alt={img?.alt_text || `Image ${i + 1}`}
                          onError={(e) => {
                            e.currentTarget.src = imageCustomSquare;
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>

                <div className="gall-thumbs">
                  {productImages.map((img: any, i: number) => (
                    <button
                      key={i}
                      type="button"
                      className={`gall-thumb ${i === activeIndex ? "is-active" : ""}`}
                      onClick={() => swiperRef.current?.slideTo?.(i)}
                    >
                      <img
                        src={getProductImageSrc(img)}
                        alt={`Thumb ${i + 1}`}
                        onError={(e) => {
                          e.currentTarget.src = imageCustomSquare;
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
        <div className="col-xl-6 min-w-0">
          <div className="mb-3">
            <h4 className="p2-name mb-3 wrap-anywhere1">
              {selectedVariant?.variant_name || productobj?.product?.name}
            </h4>
            <hr />
            {/* <h5 className="p2-price mb-3" style={{ fontSize: "1.5rem" }}>
              $ {Number(selectedVariant?.retail_price || productobj?.product?.retail_price).toFixed(2)}
            </h5> */}
            <h5 className="p2-price mb-3" style={{ fontSize: "1.5rem" }}>
              {money(discountedUnitPrice)}
              {chosen && (
                <small className="text-muted ms-2">
                  <s>{money(basePrice)}</s>
                </small>
              )}
            </h5>

            <h6 className="p2-sku mb-3" style={{ fontSize: "1.2rem" }}>
              SKU: {selectedVariant?.sku || productobj?.product?.sku}
            </h6>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              {loadingReviewSummary ? (
                <span className="p2-pill p2-pill-review">
                  <i className="ri-star-fill me-1" />
                  <span className="spinner-border spinner-border-sm me-1" style={{ width: '12px', height: '12px' }} />
                  Loading...
                </span>
              ) : reviewSummary && reviewSummary.rating_count > 0 ? (
                <span className="p2-pill p2-pill-review">
                  <i className="ri-star-fill me-1" />
                  {reviewSummary.average_rating.toFixed(1)} <span className="text-muted ms-1">({reviewSummary.rating_count} {reviewSummary.rating_count === 1 ? 'review' : 'reviews'})</span>
                </span>
              ) : (
                <span className="p2-pill p2-pill-review">
                  <i className="ri-star-fill me-1" />
                  No reviews yet
                </span>
              )}
              <span className="p2-pill p2-pill-stock">In Stock</span>
              <span className="p2-pill p2-pill-popular">Popular</span>
            </div>
          </div>
          <div className="p2-card p-4 mb-4 shadow-lg">
            <div className="">Product Attributes</div>
            <div className="mb-3">
              {productobj?.product?.variants && (
                <div className="row mt-4 gap-lg-3">
                  {Object.keys(availableAttributes).map((attrKey) => (
                    <div className="col-lg-3 col-sm-6 ms-2 text-sm" id="row-overlap" key={attrKey}>
                      <label>{attrKey}</label>
                      {/* <select
                        className="form-control table-cells"
                        value={selectedAttributes[attrKey] ?? "All"}
                        onChange={(e) => handleAttributeChange(attrKey, e.target.value)}
                      >
                        <option value="All">All</option>
                        {availableAttributes[attrKey].map((value: string) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select> */}
                      <select
                        className="form-control table-cells form-select po-select"
                        value={selectedAttributes[attrKey] ?? availableAttributes[attrKey]?.[0] ?? ""}  // << here
                        onChange={(e) => handleAttributeChange(attrKey, e.target.value)}
                      >
                        <option value="All">All</option>
                        {availableAttributes[attrKey].map((value: string) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {customizationFields?.length > 0 && (
            <div className="p2-card p-4 mb-4 shadow-lg">
              <div className="p2-card-title">Product Customization</div>

              <div className="mt-2">
                {customizationFields.map((field: any, i: number) => {
                  const name = field.field_name;

                  // value can be a File or string; prefer blob preview when present
                  const value = (formik.values as any)[name];
                  const idString = typeof value === "string" ? value.trim() : "";
                  const previewSrc =
                    customPreviews[name] ||
                    (idString ? (getImageSrc(idString, imageUrlMap) || buildImageUrl(idString)) : "");

                  const showError =
                    !!formik.touched[name] && typeof (formik.errors as any)[name] === "string";

                  return (
                    <div className="mb-3" key={i}>
                      <label className="p2-label">
                        {field.field_name} {field.required && <span className="text-danger">*</span>}
                      </label>

                      {field.field_type === "text" ? (
                        <>
                          <input
                            type="text"
                            name={name}
                            value={(formik.values as any)[name] || ""}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`p2-input form-control ${showError ? "is-invalid" : ""}`}
                            placeholder={`Enter ${field.field_name.toLowerCase()}`}
                          />
                        </>
                      ) : (
                        <>
                          <label className={`p2-file ${showError ? "is-invalid" : ""}`}>
                            <i className="ri-upload-2-line me-2" />
                            Choose File
                            <input
                              type="file"
                              name={name}
                              className="d-none"
                              accept="image/*"
                              onChange={(e) => {
                                const f = e.currentTarget.files?.[0] || null;

                                // clear old blob preview if exists
                                if (customPreviews[name]) {
                                  try { URL.revokeObjectURL(customPreviews[name]); } catch { }
                                }

                                if (f) {
                                  const url = URL.createObjectURL(f);
                                  setCustomPreviews((prev) => ({ ...prev, [name]: url }));
                                  formik.setFieldValue(name, f);
                                } else {
                                  setCustomPreviews((prev) => {
                                    const next = { ...prev };
                                    delete next[name];
                                    return next;
                                  });
                                  formik.setFieldValue(name, null);
                                }
                              }}
                            />
                          </label>

                          {/* inline preview + remove */}
                          {previewSrc && (
                            <div
                              className="position-relative mt-2 d-inline-block"
                              style={{ width: 120 }}
                            >
                              <img
                                src={previewSrc}
                                alt={field.field_name}
                                className="rounded"
                                style={{ maxWidth: 120, height: "auto", display: "block" }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 p-0"
                                style={{ width: 24, height: 24, lineHeight: 1 }}
                                title="Remove"
                                onClick={() => {
                                  // clear blob preview
                                  if (customPreviews[name]) {
                                    try { URL.revokeObjectURL(customPreviews[name]); } catch { }
                                  }
                                  setCustomPreviews((prev) => {
                                    const next = { ...prev };
                                    delete next[name];
                                    return next;
                                  });
                                  // clear form value
                                  formik.setFieldValue(name, null);
                                }}
                              >
                                <i className="bi bi-x-lg" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Template Designs — hide "Customize" when returning from Studio with design + preview ids */}
          {!isStudioCustomizedProduct && (
            <TemplateDesignsSection
              productId={productobj?.product_id || ""}
              variantId={selectedVariant?.variant_id || ""}
              signageId={signageId || ""}
            />
          )}

          {/* Volume discounts table (only when discounts exist) */}
          {volumeDiscounts.length > 0 && (
            <div className="p2-card p-4 mb-4 shadow-lg">
              <div className="text-center fw-semibold mb-2">Volume discounts</div>
              <div className="table-responsive">
                <table className="table table-bordered text-center align-middle mb-0">
                  <thead>
                    <tr className="bg-light">
                      <th style={{ width: 140 }}>Quantity</th>
                      <th style={{ width: 160 }}>Unit price</th>
                      <th>You Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {volumeDiscounts.map((d) => {
                      const unit = unitPriceFromDiscount(basePrice, d);
                      const startingAt = getStartingAt(d);
                      const save = Math.max(0, basePrice - unit) * startingAt;
                      return (
                        <tr key={d.id}>
                          <td>{startingAt}</td>
                          <td>{money(unit)}</td>
                          <td>Up to {money(save)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}



          <div className="p2-card p-4 mb-4 shadow-lg">
            <div className="d-flex flex-wrap align-items-center  gap-3">
              {/* Quantity */}
              <div className="d-flex align-items-center">
                <div className="qty-label me-3">Quantity:</div>
                <div className="qty-wrap">
                  <button type="button" className="qty-btn" onClick={handleDecrease}>−</button>
                  <input
                    type="number"
                    className="qty-val qty-input"
                    value={quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setQuantity(val < 1 ? 1 : val);
                    }}
                    min="1"
                    style={{ width: '50px' }}
                  />
                  <button type="button" className="qty-btn" onClick={handleIncrease}>+</button>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex align-items-center gap-3">
                {/* <button
                  type="button"
                  className="btn btn-pill btn-ghost"
                  onClick={() => setShowReviewModal(true)}
                >
                  <i className="ri-chat-3-line me-2" />
                  Write Review
                </button> */}
                <button
                  disabled={
                    hasRequiredFields
                      ? !(formik.dirty && formik.isValid)
                      : false// ✅ if no fields, allow Add to Cart
                  }
                  onClick={async () => {
                    const uploadedFiles: Record<string, string> = {};
                    const filesToUpload = customizationFields
                      .filter((f: any) => f.field_type === "file" && (formik.values[f.field_name] instanceof File))
                      .map((f: any) => ({ key: f.field_name, file: formik.values[f.field_name] as File }));

                    if (filesToUpload.length) {
                      // ✅ get a token from your component state or localStorage
                      const tok =
                        (token && String(token).trim()) ||
                        String(getAuthCookie("sessionToken") || "").trim();

                      if (!tok) {
                        showError("Session expired. Please sign in again to upload files.");
                        // optional: throw or early return so you don't submit without uploads
                        throw new Error("Missing auth token");
                      }

                      // ✅ pass token to the helper
                      const ids: string[] = await uploadFilesAndGetIds(filesToUpload.map(x => x.file), tok);

                      // keep your original mapping
                      filesToUpload.forEach((x, i) => {
                        uploadedFiles[x.key] = ids[i] || "";
                      });
                    }

                    // Create full customizations.fields array with value filled
                    const customizationFieldValues = customizationFields.map((field: any) => ({
                      _id: field._id, // Or use actual _id if you have it
                      field_name: field.field_name,
                      field_type: field.field_type,
                      required: field.required,
                      value:
                        field.field_type === "file"
                          ? uploadedFiles[field.field_name] || ""
                          : formik.values[field.field_name] || "",
                    }));

                    const customizations = {
                      template_name: productobj?.product?.name || "",
                      fields: customizationFieldValues,
                    };

                    if (productobj?.product) {
                      const inventoryItem = selectedVariant?.inventory?.[0] as StorageLocation;
                      const hasStudioCustomizationPayload = Boolean(
                        studioCustomizeDesignId && studioDesignName && studioPreviewId
                      );
                      const payloadImages = hasStudioCustomizationPayload
                        ? [{ url: studioPreviewId }]
                        : (productobj?.product?.images || []);
                      const payloadImageUrl = hasStudioCustomizationPayload
                        ? studioPreviewId
                        : ((productobj?.product?.images?.length ?? 0) > 0 ? productobj.product.images[0]?.url : "");

                      const pickDims = () => {
                        const v = selectedVariant?.package_dimensions;
                        const p = productobj?.product?.package_dimensions;
                        const src = v || p || {};
                        return {
                          length: Number(src?.length || 0),
                          width: Number(src?.width || 0),
                          height: Number(src?.height || 0),
                          // keep the raw weight + weight_type you store on the product
                          weight: Number(src?.weight || 0),
                          weight_type: String(src?.weight_type || "").toUpperCase(), // e.g. "G", "KG", "LBS"
                        };
                      };
                      const tax_details = {
                        tax_rate: productobj?.product?.tax_rule
                          ? productobj?.product?.tax_rule.taxRate
                          : 0,
                        tax_amount: 0,
                        applied_state: productobj?.product?.tax_rule
                          ? productobj?.product?.tax_rule.state
                          : "",
                      };
                      const is_taxable = productobj?.product?.is_taxable;
                      handleAddToCart({
                        product_id: productobj?.product_id || "",
                        variant_id: selectedVariant?.variant_id || "",
                        supplier_id: signageId || "",
                        warehouse_id:
                          selectedVariant?.inventory[0]?.warehouse_id ||
                          "",
                        name: selectedVariant?.variant_name || "",
                        sku: selectedVariant?.sku || "",
                        product_type: productobj?.product?.product_type || "variable",
                        unit_purchase_price: Number(
                          selectedVariant?.purchase_price || productobj?.product?.purchase_price
                        ),
                        unit_retail_price: Number(
                          selectedVariant?.retail_price ||
                          productobj?.product?.retail_price
                        ),
                        // unit_retail_price: discountedUnitPrice,
                        // unit_retail_price: productobj?.product?.retail_price || 0,
                        changed_unit_price: discountedUnitPrice,
                        amount: Number(discountedUnitPrice) * Number(quantity),
                        quantity: quantity,
                        package_dimensions: pickDims(),
                        tax_details,
                        is_taxable: typeof is_taxable === "boolean" ? is_taxable : true,
                        item_tax_info: buildCartItemTaxInfo(tax_details, is_taxable),
                        tax_rule: productobj?.product?.tax_rule || null,
                        tax_group: productobj?.product?.tax_group || null,
                        images: payloadImages,
                        is_production: productobj?.product?.is_production || false,
                        image_url: payloadImageUrl,
                        // amount: 0,
                        customizations: customizations,
                        design_id: studioCustomizeDesignId,
                        design_name: studioDesignName,
                        preview_url: studioPreviewId,
                        storage_location: {
                          // available: inventoryItem?.available || 0,
                          // reserved: inventoryItem?.reserved || 0,
                          // on_hand: inventoryItem?.on_hand || 0,
                          bin_id: inventoryItem?.bin_id || "",
                          bin: inventoryItem?.bin_name || "",
                          rack_id: inventoryItem?.rack_id || "",
                          rack: inventoryItem?.rack_name || "",
                          warehouse_id: inventoryItem?.warehouse_id || "",
                          warehouse: inventoryItem?.warehouse_name || "",
                          zone_id: inventoryItem?.zone_id || "",
                          zone: inventoryItem?.zone_name || "",
                        },
                      });
                    }
                  }}
                  className="btn btn-primary table-addbutton"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 mb-5">
          <div className="desc-card shadow-lg">
            <div className="desc-title">Description:</div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="desc-tabbar d-flex flex-wrap gap-2">
                <button
                  className={`desc-tab ${descTab === 'specs' ? 'is-active' : ''}`}
                  onClick={() => setDescTab('specs')}
                  type="button"
                >
                  Specification
                </button>
                <button
                  className={`desc-tab ${descTab === 'details' ? 'is-active' : ''}`}
                  onClick={() => setDescTab('details')}
                  type="button"
                >
                  Details
                </button>
                <button
                  className={`desc-tab ${descTab === 'reviews' ? 'is-active' : ''}`}
                  onClick={() => setDescTab('reviews')}
                  type="button"
                >
                  Reviews ({loadingReviewSummary ? '...' : (reviewSummary?.rating_count || 0)})
                </button>
              </div>

              <button
                type="button"
                className="btn btn-pill btn-ghost ms-auto"
                onClick={() => setShowReviewModal(true)}
              >
                <i className="ri-chat-3-line me-2" />
                Write Review
              </button>
            </div>

            <div className="desc-body">
              {descLoading && (
                <div className="desc-loading">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading…
                </div>
              )}

              {!!descError && <div className="alert alert-danger py-2 px-3">{descError}</div>}

              {descTab === 'specs' && !descLoading && (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <tbody>
                      {specLeft.map((row, i) => (
                        <tr key={`l-${i}`}>
                          <th className="desc-key">{row.k}</th>
                          <td className="desc-val">{row.v}</td>
                          {/* <th className="desc-key">{specRight[i]?.k}</th> */}
                          {/* <td className="desc-val">{specRight[i]?.v}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {descTab === 'details' && !descLoading && (
                <div className="desc-details">
                  {detailsHtml ? (
                    <div className="desc-details-html wrap-anywhere" dangerouslySetInnerHTML={{ __html: detailsHtml }} />
                  ) : (
                    <>
                      <div className="alert alert-danger py-2 px-3 mt-2 mb-0">
                        NO DESCRIPTION AVAILABLE
                      </div>
                      {/* <p className="mb-3">
                        Our standard yard sign is perfect for residential property listings. Made with
                        high-quality corrugated plastic and UV-resistant inks for long-lasting outdoor use.
                      </p>
                      <div className="row g-4">
                        <div className="col-lg-6">
                          <div className="desc-subtitle">Features:</div>
                          <ul className="desc-list">
                            <li>Weather resistant construction</li>
                            <li>Vibrant, fade-resistant colors</li>
                            <li>Easy installation with H-stakes</li>
                            <li>Custom design options available</li>
                            <li>Bulk pricing available</li>
                          </ul>
                        </div>
                        <div className="col-lg-6">
                          <div className="desc-subtitle">Usage:</div>
                          <p>Ideal for residential listings, open houses, and property marketing</p>
                          <div className="desc-subtitle mt-3">Warranty:</div>
                          <p>30-day quality guarantee</p>
                        </div>
                      </div> */}
                    </>
                  )}
                </div>
              )}

              {descTab === 'reviews' && !descLoading && (
                <div className="desc-reviews">
                  {(reviews.length ? reviews : [
                    // {
                    //   id: 'seed1',
                    //   user_name: 'Sarah Johnson',
                    //   rating: 5,
                    //   date: '2024-01-15',
                    //   title: 'Excellent quality sign!',
                    //   body:
                    //     'The sign quality exceeded my expectations. The colors are vibrant and the material feels very durable. Perfect for our property listings.',
                    //   verified: true,
                    //   recommends: true,
                    //   helpful_yes: 12,
                    //   helpful_no: 0,
                    // },
                    // {
                    //   id: 'seed2',
                    //   user_name: 'Mike Chen',
                    //   rating: 4,
                    //   date: '2024-01-10',
                    //   title: 'Good value for money',
                    //   body:
                    //     'Great sign for the price. Installation was easy and it looks professional. Only wish it came with more mounting options.',
                    //   verified: false,
                    //   recommends: true,
                    //   helpful_yes: 8,
                    //   helpful_no: 0,
                    // },
                  ]).map((rv) => (
                    <div className="desc-review" key={rv.id}>
                      <div className="d-flex align-items-start gap-3">
                        <div className="desc-avatar"><i className="ri-user-3-line" /></div>
                        <div className="flex-grow-1">
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="fw-semibold">{rv.user_name}</div>
                            {rv.verified && <span className="desc-chip desc-chip-green">Verified</span>}
                            <div className="text-muted small">{new Date(rv.date).toLocaleDateString()}</div>
                          </div>

                          <div className="desc-stars mt-1">
                            {'★★★★★'.slice(0, rv.rating)}
                            <span className="text-muted ms-2">{rv.rating} out of 5</span>
                          </div>

                          {rv.title && <div className="mt-2 fw-semibold">{rv.title}</div>}
                          <p className="mb-3 text-muted">{rv.body}</p>

                          {rv.recommends && (
                            <span className="desc-chip desc-chip-blue">
                              <i className="ri-checkbox-circle-line me-1" />
                              Recommends this product
                            </span>
                          )}

                          <hr className="my-3" />
                          <div className="d-flex align-items-center gap-3">
                            <span className="text-muted small">Was this helpful?</span>
                            <button type="button" className="desc-help">
                              <i className="ri-thumb-up-line me-1" /> Yes ({rv.helpful_yes || 0})
                            </button>
                            <button type="button" className="desc-help">
                              <i className="ri-thumb-down-line me-1" /> No ({rv.helpful_no || 0})
                            </button>
                          </div>
                        </div>
                        <button type="button" className="btn btn-link p-0 text-muted">
                          <i className="ri-more-2-fill" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <ReviewModal
        show={showReviewModal}
        vendor={productobj ? {
          name: productobj.product?.name || "Product",
          id: productobj.product_id || "",
          logo: productobj.product?.images?.[0]?.url
        } : null}
        logoUrlMap={imageUrlMap || {}}
        onClose={() => setShowReviewModal(false)}
        reviewType="product"
        // ⬇️ NEW: pass ids for the product review API
        productId={productobj?.product_id || ""}
        productVariantId={productobj?.product?.variants?.[0]?.variant_id}
        onSubmit={async () => {
          await loadAllProductReviews();  // re-fetch so the new review appears in the list
          // Refresh review summary after successful submission
          const prodId = productobj?.product_id;
          const variantId = selectedVariant?.variant_id || productobj?.product?.variants?.[0]?.variant_id;
          if (prodId && variantId) {
            await fetchReviewSummary(prodId, variantId);
          }
        }}
      />

      {showModal && (
        <div className="cart-modal-overlay active" onClick={() => setShowModal(false)} role="presentation">
          <div className="cart-modal active" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="cart-modal-title">
            <div className="cart-modal-header">
              <div className="cart-modal-header-content">
                <div className="cart-success-icon">✓</div>
                <div>
                  <div className="cart-modal-title" id="cart-modal-title">Product successfully added to the Cart</div>
                  <div className="cart-modal-subtitle">Your item has been added and is ready for checkout</div>
                </div>
              </div>
              <button type="button" className="cart-modal-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>

            <div className="cart-modal-body">
              <div className="cart-product-label">Product Details</div>
              <div className="cart-product-details">
                <div className="cart-product-image">
                  {studioPreviewPresignedUrl ? (
                    <img src={studioPreviewPresignedUrl} alt="Customized Design" style={{ width: 80, height: 100, objectFit: "contain", borderRadius: 8 }} />
                  ) : (firstId || fallbackId) && src ? (
                    <img src={src} alt={modalTitle} style={{ width: 80, height: 100, objectFit: "contain", borderRadius: 8 }} />
                  ) : (
                    <span style={{ color: "var(--text-light)" }}>📦</span>
                  )}
                </div>
                <div className="cart-product-info">
                  <div className="cart-product-name">{modalTitle}</div>
                  <div className="cart-product-meta">
                    <div className="cart-product-meta-first-row">
                      <div className="cart-meta-group">
                        <span className="cart-meta-label">Quantity:</span>
                        <span className="cart-meta-value">{modalQty}</span>
                      </div>
                      <div className="cart-meta-group">
                        <span className="cart-meta-label">Unit Price:</span>
                        <span className="cart-meta-price">{money(modalUnitPrice)}</span>
                      </div>
                    </div>
                    <div className="cart-product-meta-row cart-product-sku">
                      <span className="cart-meta-label">SKU:</span>
                      <span className="cart-meta-value">{modalSku || "—"}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cart-subtotal">
                <span className="cart-subtotal-label">Subtotal:</span>
                <span className="cart-subtotal-value">{money(modalSubtotal)}</span>
              </div>
            </div>

            <div className="cart-modal-actions">
              <button
                type="button"
                className="btn-continue-shopping"
                onClick={() => {
                  setShowModal(false);
                  navigate(-3);
                }}
              >
                Continue Shopping
              </button>
              <Link
                to="/agent/order/checkout"
                className="btn-proceed-checkout"
                style={{ display: "block", textAlign: "center", textDecoration: "none", color: "white" }}
                onClick={() => setShowModal(false)}
              >
                Proceed to Checkout
              </Link>
            </div>
          </div>
        </div>
      )}

    </div>

  );

  // return (
  //   <div className="content">
  //     <div className="card">
  //       <div className="card-body">
  //         <h2 className="ps-4">Product Details </h2>
  //       </div>
  //     </div>

  //     <div className="row">
  //       <div className="col-lg-12">
  //         <div className="card">
  //           <div className="card-body">
  //             <div className="row gx-lg-5">
  //               <div className="col-xl-4 col-md-5 mx-auto">
  //                 {/* <div className="product-img-slider sticky-side-div">
  //                        <div className="swiper product-thumbnail-slider p-2 rounded bg-light">
  //                          <div className="swiper-wrapper">
  //                            <div className="swiper-slide">
  //                              <img
  //                                src={`${process.env.REACT_APP_BASE_URL}${
  //                                  productobj?.product?.image?.url || ""
  //                                }`}
  //                                alt=""
  //                                className="img-fluid d-block"
  //                              />
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <img
  //                                src={`${process.env.REACT_APP_BASE_URL}${
  //                                  productobj?.product?.image?.url || ""
  //                                }`}
  //                                alt=""
  //                                className="img-fluid d-block"
  //                              />
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <img
  //                                src={`${process.env.REACT_APP_BASE_URL}${
  //                                  productobj?.product?.image?.url || ""
  //                                }`}
  //                                alt=""
  //                                className="img-fluid d-block"
  //                              />
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <img
  //                                src={`${process.env.REACT_APP_BASE_URL}${
  //                                  productobj?.product?.image?.url || ""
  //                                }`}
  //                                alt=""
  //                                className="img-fluid d-block"
  //                              />
  //                            </div>
  //                          </div>
  //                          <div className="swiper-button-next material-shadow"></div>
  //                          <div className="swiper-button-prev material-shadow"></div>
  //                        </div>
  //                        <div className="swiper product-nav-slider mt-2">
  //                          <div className="swiper-wrapper">
  //                            <div className="swiper-slide">
  //                              <div className="nav-slide-item">
  //                                <img
  //                                  src={`${process.env.REACT_APP_BASE_URL}${
  //                                    productobj?.product?.image?.url || ""
  //                                  }`}
  //                                  alt=""
  //                                  className="img-fluid d-block"
  //                                />
  //                              </div>
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <div className="nav-slide-item">
  //                                <img
  //                                  src={`${process.env.REACT_APP_BASE_URL}${
  //                                    productobj?.product?.image?.url || ""
  //                                  }`}
  //                                  alt=""
  //                                  className="img-fluid d-block"
  //                                />
  //                              </div>
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <div className="nav-slide-item">
  //                                <img
  //                                  src={`${process.env.REACT_APP_BASE_URL}${
  //                                    productobj?.product?.image?.url || ""
  //                                  }`}
  //                                  alt=""
  //                                  className="img-fluid d-block"
  //                                />
  //                              </div>
  //                            </div>
  //                            <div className="swiper-slide">
  //                              <div className="nav-slide-item">
  //                                <img
  //                                  src={`${process.env.REACT_APP_BASE_URL}${
  //                                    productobj?.product?.image?.url || ""
  //                                  }`}
  //                                  alt=""
  //                                  className="img-fluid d-block"
  //                                />
  //                              </div>
  //                            </div>
  //                          </div>
  //                        </div>
  //                      </div> */}
  //                 <div className="product-gallery">
  //                   {/* Main Image Swiper */}
  //                   <div className="main-image-container">
  //                     <Swiper
  //                       modules={[Navigation]}
  //                       navigation
  //                       onSwiper={(swiper: any) => (swiperRef.current = swiper)}
  //                       onSlideChange={(swiper: any) => setActiveIndex(swiper.activeIndex)}
  //                       slidesPerView={1}
  //                       spaceBetween={10}
  //                     >
  //                       {productImages?.map((img, index) => (
  //                         <SwiperSlide key={index}>
  //                           <img
  //                             // src={`${process.env.REACT_APP_BASE_URL}${img.url}`}
  //                             alt={img.alt_text || `Slide ${index + 1}`}
  //                             className="img-fluid rounded shadow-sm w-100"
  //                             style={{ maxHeight: '1000px', objectFit: 'contain' }}
  //                           />
  //                         </SwiperSlide>
  //                       ))}
  //                     </Swiper>
  //                   </div>

  //                   {/* Thumbnails */}
  //                   <div className="thumbnails-wrapper mt-3">
  //                     <div className="d-flex justify-content-center gap-2 flex-wrap">
  //                       {productImages?.map((img, index) => (
  //                         <img
  //                           key={index}
  //                           // src={`${process.env.REACT_APP_BASE_URL}${img.url}`}
  //                           alt={`Thumb ${index + 1}`}
  //                           className={`thumbnail-image ${index === activeIndex ? "active" : ""}`}
  //                           onClick={() => swiperRef.current?.slideTo(index)}
  //                         />
  //                       ))}
  //                     </div>
  //                   </div>
  //                 </div>

  //               </div>

  //               <div className="col-xl-7">
  //                 <div className="mt-xl-0 mt-5">
  //                   <div className="d-flex">
  //                     <div className="mb-4">
  //                       <h4 className="producttittle mb-2">
  //                         {selectedVariant?.variant_name || productobj?.product?.name}
  //                       </h4>
  //                       <hr />
  //                       <h5 className="mt-3 mb-2 text-primary" style={{ fontSize: "1.5rem" }}>
  //                         $ {Number(selectedVariant?.retail_price || productobj?.product?.retail_price).toFixed(2)}
  //                       </h5>
  //                       <h6 className="mb-0 mt-3 text-muted" style={{ fontSize: "1.2rem" }}>
  //                         SKU: {selectedVariant?.sku || productobj?.product?.sku}
  //                       </h6>
  //                     </div>
  //                   </div>

  //                   <div className="row mt-3" id="row-overlap">
  //                     <h5 className="table-headers ms-2" id="row-overlap">Available Combinations:</h5>
  //                     {productobj?.product?.variants && (
  //                       <div className="row mt-4 gap-lg-3">
  //                         {Object.keys(availableAttributes)?.map((attrKey) => (
  //                           <div className="col-lg-3 col-sm-6  table-cells" id="row-overlap" key={attrKey}>
  //                             <label>{attrKey}</label>
  //                             <select
  //                               className="form-control table-cells"
  //                               value={selectedAttributes[attrKey] || ""}
  //                               onChange={(e) =>
  //                                 handleAttributeChange(attrKey, e.target.value)
  //                               }
  //                             >
  //                               {availableAttributes[attrKey]?.map((value) => (
  //                                 <option key={value} value={value}>
  //                                   {value}
  //                                 </option>
  //                               ))}
  //                             </select>
  //                           </div>
  //                         ))}
  //                       </div>
  //                     )}
  //                   </div>
  //                   <div className="row mt-3">
  //                     <div className="col-md-8 ms-3 mt-3" id="row-overlap">
  //                       <div className="col-md-6">
  //                         <form>
  //                           {customizationFields?.length > 0 && (
  //                             <div className="mt-4 mb-4">
  //                               <h5 className="mb-2">Product customization</h5>
  //                               {customizationFields.map((field, index) => (
  //                                 <div key={index} className="mb-3">
  //                                   <label className="form-label">
  //                                     {field.field_name}{" "}
  //                                     {field.required === true && (
  //                                       <span className="text-danger">*</span>
  //                                     )}
  //                                   </label>
  //                                   {field.field_type === "text" ? (
  //                                     <>
  //                                       <input
  //                                         type="text"
  //                                         name={field.field_name}
  //                                         className="form-control"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values[field.field_name] || ""}
  //                                       />
  //                                       {formik.touched[field.field_name] &&
  //                                         formik.errors[field.field_name] &&
  //                                         typeof formik.errors[field.field_name] === "string" && (
  //                                           <div className="text-danger">
  //                                             {formik.errors[field.field_name] as string}
  //                                           </div>
  //                                         )}
  //                                     </>
  //                                   ) : (
  //                                     <>
  //                                       <input
  //                                         type="file"
  //                                         name={field.field_name}
  //                                         className="form-control"
  //                                         onChange={(e) => {
  //                                           const file = e.currentTarget.files?.[0];
  //                                           if (file) {
  //                                             formik.setFieldValue(field.field_name, file);
  //                                             setFilePreviews((prev) => ({
  //                                               ...prev,
  //                                               [field.field_name]: URL.createObjectURL(file),
  //                                             }));
  //                                           }
  //                                         }}
  //                                       />
  //                                       {filePreviews[field.field_name] && (
  //                                         <img
  //                                           src={filePreviews[field.field_name]}
  //                                           alt="Preview"
  //                                           style={{ maxWidth: "100px", marginTop: "8px" }}
  //                                         />
  //                                       )}
  //                                       {formik.touched[field.field_name] &&
  //                                         formik.errors[field.field_name] &&
  //                                         typeof formik.errors[field.field_name] === "string" && (
  //                                           <div className="text-danger">
  //                                             {formik.errors[field.field_name] as string}
  //                                           </div>
  //                                         )}
  //                                     </>
  //                                   )}
  //                                 </div>
  //                               ))}
  //                             </div>
  //                           )}
  //                         </form>

  //                       </div>
  //                     </div>
  //                   </div>

  //                   <div className="row mt-3">
  //                     <div className="col-md-6 ms-3" id="row-overlap">
  //                       <div className="quantity-container d-flex align-items-center">
  //                         <label className="quantity-label me-2">Quantity :</label>
  //                         <div className="quantitybutton">
  //                           <button
  //                             type="button"
  //                             className="quantityaddsubbutton"
  //                             onClick={handleDecrease}
  //                           >
  //                             −
  //                           </button>

  //                           <span
  //                             className="quantitytext"
  //                           >
  //                             {quantity}
  //                           </span>

  //                           <button
  //                             type="button"
  //                             className="quantityaddsubbutton"
  //                             onClick={handleIncrease}
  //                           >
  //                             +
  //                           </button>
  //                         </div>

  //                       </div>


  //                     </div>
  //                   </div>

  //                   <div className="row mb-3 rcust-margin-4">
  //                     <div className="col-md-6 d-flex justify-content-between">
  //                       <div></div>
  //                       <button
  //                         disabled={
  //                           hasRequiredFields
  //                             ? !(formik.dirty && formik.isValid)
  //                             : false // ✅ if no fields, allow Add to Cart
  //                         }
  //                         onClick={async () => {
  //                           // Upload any file inputs
  //                           const uploadedFiles: Record<string, string> = {};
  //                           for (const field of customizationFields) {
  //                             if (
  //                               field.field_type === "file" &&
  //                               formik.values[field.field_name] instanceof File
  //                             ) {
  //                               const path = await uploadFile(formik.values[field.field_name] as File);
  //                               uploadedFiles[field.field_name] = path;
  //                             }
  //                           }

  //                           // Create full customizations.fields array with value filled
  //                           const customizationFieldValues = customizationFields?.map((field: any) => ({
  //                             _id: field._id, // Or use actual _id if you have it
  //                             field_name: field.field_name,
  //                             field_type: field.field_type,
  //                             required: field.required,
  //                             value:
  //                               field.field_type === "file"
  //                                 ? uploadedFiles[field.field_name] || ""
  //                                 : formik.values[field.field_name] || "",
  //                           }));

  //                           const customizations = {
  //                             template_name: productobj?.product?.name || "",
  //                             fields: customizationFieldValues,
  //                           };

  //                           if (productobj?.product) {
  //                             const inventoryItem = selectedVariant?.inventory?.[0] as StorageLocation;
  //                             handleAddToCart({
  //                               product_id: productobj?.product_id || "",
  //                               variant_id: selectedVariant?.variant_id || "",
  //                               warehouse_id:
  //                                 selectedVariant?.inventory[0]?.warehouse_id ||
  //                                 "",
  //                               name: selectedVariant?.variant_name || "",
  //                               sku: selectedVariant?.sku || "",
  //                               unit_purchase_price: Number(
  //                                 selectedVariant?.purchase_price || productobj?.product?.purchase_price
  //                               ),
  //                               unit_retail_price: Number(
  //                                 selectedVariant?.retail_price ||
  //                                 productobj?.product?.retail_price
  //                               ),
  //                               quantity: quantity,
  //                               tax_details: {
  //                                 tax_rate: productobj?.product?.tax_rule
  //                                   ? productobj?.product?.tax_rule.taxRate
  //                                   : 0,
  //                                 tax_amount: 0,
  //                                 applied_state: productobj?.product?.tax_rule
  //                                   ? productobj?.product?.tax_rule.state
  //                                   : "",
  //                               },
  //                               tax_rule: productobj?.product?.tax_rule || null,
  //                               tax_group: productobj?.product?.tax_group || null,
  //                               image_url: selectedVariant?.image?.url || "",
  //                               amount: 0,
  //                               customizations: customizations,
  //                               storage_location: {
  //                                 // available: inventoryItem?.available || 0,
  //                                 // reserved: inventoryItem?.reserved || 0,
  //                                 // on_hand: inventoryItem?.on_hand || 0,
  //                                 bin_id: inventoryItem?.bin_id || "",
  //                                 bin: inventoryItem?.bin_name || "",
  //                                 rack_id: inventoryItem?.rack_id || "",
  //                                 rack: inventoryItem?.rack_name || "",
  //                                 warehouse_id: inventoryItem?.warehouse_id || "",
  //                                 warehouse: inventoryItem?.warehouse_name || "",
  //                                 zone_id: inventoryItem?.zone_id || "",
  //                                 zone: inventoryItem?.zone_name || "",
  //                               },
  //                             });
  //                           }
  //                         }}
  //                         className="btn btn-success table-cells"
  //                       >
  //                         Add to Cart
  //                       </button>
  //                     </div>
  //                   </div>
  //                   <div className="mt-5 text-muted">
  //                     {/* <h5 className="fs-14 table-headers">Description :</h5> */}
  //                     <p>{productobj?.product?.description}</p>
  //                   </div>

  //                   {/* <div className="row">
  //                                          <div className="col-sm-6">
  //                                            <div className="mt-3">
  //                                              <h5 className="fs-14">Features :</h5>
  //                                              <ul className="list-unstyled">
  //                                                <li className="py-1">
  //                                                  <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                  Full Sleeve
  //                                                </li>
  //                                                <li className="py-1">
  //                                                  <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                  Cotton
  //                                                </li>
  //                                                <li className="py-1">
  //                                                  <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                  All Sizes available
  //                                                </li>
  //                                                <li className="py-1">
  //                                                  <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                  4 Different Color
  //                                                </li>
  //                                              </ul>
  //                                            </div>
  //                                          </div>
  //                                          <div className="col-sm-6">
  //                                            <div className="mt-3">
  //                                              <h5 className="fs-14">Services :</h5>
  //                                              <ul className="list-unstyled product-desc-list">
  //                                                <li className="py-1">10 Days Replacement</li>
  //                                                <li className="py-1">Cash on Delivery available</li>
  //                                              </ul>
  //                                            </div>
  //                                          </div>
  //                                        </div> */}

  //                   <div className="product-content mt-5">
  //                     {/* <h5 className="fs-14 mb-3">Product Description :</h5> */}
  //                     <nav>
  //                       <ul
  //                         className="nav nav-tabs nav-tabs-custom nav-success"
  //                         id="nav-tab"
  //                         role="tablist"
  //                       >
  //                         <li className="nav-item">
  //                           <a
  //                             className="nav-link active table-headers"
  //                             id="nav-speci-tab"
  //                             data-bs-toggle="tab"
  //                             href="#nav-speci"
  //                             role="tab"
  //                             aria-controls="nav-speci"
  //                             aria-selected="true"
  //                           >
  //                             Specification
  //                           </a>
  //                         </li>
  //                         <li className="nav-item">
  //                           <a
  //                             className="nav-link table-headers"
  //                             id="nav-detail-tab"
  //                             data-bs-toggle="tab"
  //                             href="#nav-detail"
  //                             role="tab"
  //                             aria-controls="nav-detail"
  //                             aria-selected="false"
  //                           >
  //                             Details
  //                           </a>
  //                         </li>
  //                       </ul>
  //                     </nav>
  //                     <div
  //                       className="tab-content border border-top-0 p-4"
  //                       id="nav-tabContent"
  //                     >
  //                       <div
  //                         className="tab-pane fade show active"
  //                         id="nav-speci"
  //                         role="tabpanel"
  //                         aria-labelledby="nav-speci-tab"
  //                       >
  //                         <div className="table-responsive">
  //                           <table className="table mb-0">
  //                             <tbody>
  //                               <tr>
  //                                 <th className="table-cells" scope="row" style={{ width: "200px" }}>
  //                                   Category
  //                                 </th>
  //                                 <td className="table-cells">{productobj?.product?.category}</td>
  //                               </tr>
  //                               <tr>
  //                                 <th className="table-cells" scope="row">Brand</th>
  //                                 <td className="table-cells">{productobj?.product?.brand}</td>
  //                               </tr>
  //                               <tr>
  //                                 <th className="table-cells" scope="row">Dimensions</th>
  //                                 <td className="table-cells">
  //                                   {
  //                                     productobj?.product?.package_dimensions
  //                                       ?.length
  //                                   }{" "}
  //                                   x{" "}
  //                                   {
  //                                     productobj?.product?.package_dimensions
  //                                       ?.width
  //                                   }{" "}
  //                                   x{" "}
  //                                   {
  //                                     productobj?.product?.package_dimensions
  //                                       ?.height
  //                                   }
  //                                 </td>
  //                               </tr>
  //                               <tr>
  //                                 <th className="table-cells" scope="row">Weight</th>
  //                                 <td className="table-cells">
  //                                   {
  //                                     productobj?.product?.package_dimensions
  //                                       ?.weight
  //                                   }{" "}
  //                                   Gram
  //                                 </td>
  //                               </tr>
  //                               <tr>
  //                                 <th className="table-cells" scope="row">Item Condition</th>
  //                                 <td className="table-cells">{productobj?.product?.item_condition}</td>
  //                               </tr>
  //                             </tbody>
  //                           </table>
  //                         </div>
  //                       </div>
  //                       <div
  //                         className="tab-pane fade"
  //                         id="nav-detail"
  //                         role="tabpanel"
  //                         aria-labelledby="nav-detail-tab"
  //                       >
  //                         <div>
  //                           <h5 className="font-size-16 mb-3">
  //                             {productobj?.product?.name}
  //                           </h5>
  //                           <p>{productobj?.product?.description}</p>
  //                           {/* <div>
  //                                                  <p className="mb-2">
  //                                                    <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                    Machine Wash
  //                                                  </p>
  //                                                  <p className="mb-2">
  //                                                    <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                    Fit Type: Regular
  //                                                  </p>
  //                                                  <p className="mb-2">
  //                                                    <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                    100% Cotton
  //                                                  </p>
  //                                                  <p className="mb-0">
  //                                                    <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
  //                                                    Long sleeve
  //                                                  </p>
  //                                                </div> */}
  //                         </div>
  //                       </div>
  //                     </div>
  //                   </div>

  //                   <button
  //                         className="btn btn-outline-secondary br-8 mt-3"
  //                         type="button"
  //                         onClick={() => navigate(-1)}
  //                       >
  //                         Back
  //                       </button>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );
};

export default AgentViewSignageVariableProduct;
